import {
  put, takeLatest, call, 
} from 'redux-saga/effects';
import { delay } from 'redux-saga';
import _ from 'lodash';
import queryString from 'query-string';
import { Base64 } from 'js-base64';
import { FETCH_PROFILE, FETCH_GOV_ID_RESPONSE } from '../constants';
import {
  fetchProfileFailure,
  fetchProfileSuccess,
  saveUserProfile,
  registerUserFailure,
  getGovIdLinkSuccess,
  getGovIdLinkFailure,
  getPrefillHintSuccess,
  getPrefillHintFailure,
} from '../actions';
import { fetchUser, getRegistrationParams } from '../../services/auth';

const GOV_ID_POLL_INTERVAL_MS = 4000;
const GOV_ID_MAX_ATTEMPTS = 30;

const createIdvSagas = (api) => {
  function* fetchGovIdResponse(action) {
    const { code, push } = action.payload;
    const queryParams = queryString.parse(window.location.search);
    const stateFromIdw = _.get(queryParams, 'state', false);
    const registrationParams = getRegistrationParams();
    let callbackUri = registrationParams.callbackUri;
    let accessToken = false;
    let clientId = registrationParams.clientId || _.get(queryParams, 'clientId', false);
    if (stateFromIdw) {
      let decodedStateQueryParamObj;
      try {
        const decodedStateQueryParam = Base64.decode(decodeURIComponent(stateFromIdw));
        decodedStateQueryParamObj = JSON.parse(decodedStateQueryParam);
        callbackUri = callbackUri || _.get(decodedStateQueryParamObj, 'callbackUri', false);
        accessToken = _.get(decodedStateQueryParamObj, 'accessToken', false);
        clientId = clientId || _.get(decodedStateQueryParamObj, 'clientId', false);
      } catch (error) {
        push('/idv/failed');
        return;
      }
    }

    const user = fetchUser();
    const token = accessToken || user.accessToken;
    const callbackUriStr = callbackUri ? `?callbackUri=${encodeURIComponent(callbackUri)}` : '';

    try {
      let terminalResult = null;
      for (let attempt = 0; attempt < GOV_ID_MAX_ATTEMPTS; attempt += 1) {
        const response = yield call(api.putRegisterVerifyWithCode, {
          code,
          accessToken: token,
        });
        const httpStatus = _.get(response, 'status', false);
        const data = _.get(response, 'data.data', {});
        const pass = _.get(data, 'pass', false);
        const hardFail = _.get(data, 'hardFail', false);
        const softFail = _.get(data, 'softFail', false);
        const lexIdDuplicate = _.get(data, 'lexIdDuplicate', false);
        const duplicatedEmail = _.get(data, 'duplicatedEmail', '');
        const vseMatch = _.get(data, 'vseMatch', false);

        if (
          httpStatus === 200 &&
          (pass || hardFail || softFail || lexIdDuplicate || duplicatedEmail || vseMatch)
        ) {
          terminalResult = {
            pass,
            hardFail,
            softFail,
            lexIdDuplicate,
            duplicatedEmail,
            vseMatch,
            httpStatus,
          };
          break;
        }

        if (httpStatus >= 400 && httpStatus < 500) {
          push(`/idv/failed${callbackUriStr}`);
          return;
        }

        yield delay(GOV_ID_POLL_INTERVAL_MS);
      }

      if (!terminalResult) {
        push(`/idv/failed${callbackUriStr}`);
        return;
      }

      const {
        pass, hardFail, softFail, lexIdDuplicate, duplicatedEmail, vseMatch, httpStatus, 
      } =
        terminalResult;

      if (pass) {
        push(`/idv/success${callbackUriStr}`);
      } else if (lexIdDuplicate || duplicatedEmail) {
        push(`/user-duplicated?clientId=${clientId}`);
        yield put(registerUserFailure(httpStatus, '', duplicatedEmail));
      } else if (vseMatch) {
        push('/registration-error');
      } else if (hardFail || softFail) {
        push(`/idv/failed${callbackUriStr}`);
      } else {
        push(`/idv/failed${callbackUriStr}`);
      }
    } catch (e) {
      push(`/idv/failed${callbackUriStr}`);
    }
  }

  function* watchFetchGovIdResponse() {
    yield takeLatest(FETCH_GOV_ID_RESPONSE, fetchGovIdResponse);
  }

  function* fetchProfileSaga(action) {
    const { isUpdateProfile } = action.payload;
    try {
      const queryParams = queryString.parse(window.location.search);
      let accessToken = _.get(queryParams, 'accessToken', false);
      if (!accessToken) {
        const user = fetchUser();
        ({ accessToken } = user);
      }
      const response = yield call(api.fetchProfile, accessToken);
      const userDataParsed = _.get(response, 'data.data.attributes', false);
      yield put(saveUserProfile({ userData: userDataParsed }));
      const statusCode = _.get(response, 'data.data.statusCode', false);
      if (statusCode === 200) {
        const profile = _.get(response, 'data.data.attributes', null);
        yield put(fetchProfileSuccess(profile, isUpdateProfile));
      } else {
        const errorDetail = _.get(response, 'data.data.message', false);
        yield put(fetchProfileFailure({ errorDetail }));
      }
    } catch (e) {
      const errorDetail = _.get(e.response, 'data.data.message', false);
      yield put(fetchProfileFailure({ errorDetail }));
    }
  }

  function* watchFetchProfile() {
    yield takeLatest(FETCH_PROFILE, fetchProfileSaga);
  }

  function* getGovIdLinkSaga(action) {
    const { govIdType, linkType } = action.payload;

    try {
      // Get the GovID link via backend
      // Backend will handle getting the IDDW token internally
      const response = yield call(api.getGovIdLink, {
        govIdType,
        linkType: linkType || 'browser', // Default to 'browser' for desktop flow
      });
      const statusCode = _.get(response, 'status', false);
      const nestedStatusCode = _.get(response, 'data.data.statusCode', false);

      if (statusCode === 200 || nestedStatusCode === 200) {
        // Try multiple possible paths for the link
        const link =
          _.get(response, 'data.data.link', false) ||
          _.get(response, 'data.data.url', false) ||
          _.get(response, 'data.link', false) ||
          _.get(response, 'data.url', false) ||
          _.get(response, 'data.body.link', false) ||
          _.get(response, 'data.body.url', false) ||
          _.get(response, 'link', false) ||
          _.get(response, 'url', false);

        if (link) {
          // Dispatch success action with the link instead of redirecting
          yield put(getGovIdLinkSuccess({ link }));
        } else {
          yield put(getGovIdLinkFailure('No link found in response'));
        }
      } else {
        const errorDetail =
          _.get(response, 'data.errors.0.message', false) ||
          _.get(response, 'data.data.message', false) ||
          _.get(response, 'data.body.message', false) ||
          _.get(response, 'data.message', false);
        yield put(getGovIdLinkFailure(errorDetail || 'Unknown error'));
      }
    } catch (e) {
      const errorDetail =
        _.get(e.response, 'data.data.message', false) ||
        _.get(e.response, 'data.errors.0.message', false);
      yield put(getGovIdLinkFailure(errorDetail || e.message || 'Unknown error'));
    }
  }

  function* watchGetGovIdLink() {
    yield takeLatest(GET_GOV_ID_LINK, getGovIdLinkSaga);
  }

  function* getPrefillHintSaga(action) {
    const {
      baseUrl, path, redirectUri, iddwClientId, encodedStateQueryParam, 
    } = action.payload;

    try {
      const response = yield call(api.getPrefillHint);
      const statusCode = _.get(response, 'status', false);
      const nestedStatusCode = _.get(response, 'data.data.statusCode', false);

      if (statusCode === 200 || nestedStatusCode === 200) {
        const loginHint =
          _.get(response, 'data.data.login_hint', false) ||
          _.get(response, 'data.login_hint', false) ||
          _.get(response, 'data.body.login_hint', false) ||
          _.get(response, 'login_hint', false);

        if (loginHint) {
          const encodedLoginHint = encodeURIComponent(loginHint);
          const redirectUrl = `${baseUrl}/${path}?client_id=${iddwClientId}&redirect_uri=${encodeURIComponent(
            redirectUri,
          )}&scope=openid+country.US&response_type=code&state=${encodedStateQueryParam}&login_hint=${encodedLoginHint}`;
          yield put(getPrefillHintSuccess({ redirectUrl }));
        } else {
          yield put(getPrefillHintFailure('No login_hint in response'));
        }
      } else {
        const errorDetail =
          _.get(response, 'data.errors.0.message', false) ||
          _.get(response, 'data.data.message', false) ||
          _.get(response, 'data.body.message', false) ||
          _.get(response, 'data.message', false);
        yield put(getPrefillHintFailure(errorDetail || 'Unknown error'));
      }
    } catch (e) {
      const errorDetail =
        _.get(e.response, 'data.data.message', false) ||
        _.get(e.response, 'data.errors.0.message', false);
      yield put(getPrefillHintFailure(errorDetail || e.message || 'Unknown error'));
    }
  }

  function* watchGetPrefillHint() {
    yield takeLatest(GET_PREFILL_HINT, getPrefillHintSaga);
  }

  return {
    watchFetchProfile,
    watchFetchGovIdResponse,
    watchGetGovIdLink,
    watchGetPrefillHint,
  };
};

export default createIdvSagas;
